/**
 * Battery optimizer (port 0x220, v2 conf) algorithm fine-tuning.
 *
 * The v2 conf carries four signed 8-bit state-penalty offsets that are added
 * to the firmware's compiled-in penalties. One offset step is 8 firmware base
 * units (SPP_BATOPT_PENALTY_OFFSET_SCALE, fw >= 444480ca) = 0.8 c/h at 15 kW
 * reference power, scaled with system power; the compiled-in defaults are
 * PBS −7, PB 45, BUY 5, SELL 40 base units. A positive offset discourages the
 * state (PREVENT), a negative one favours it (FAVOUR). `+127` is a firmware
 * sentinel: "prevent this state completely". `−128` has no special handling
 * but in practice collapses the whole plan into that state.
 *
 * The UI shows each offset as a 21-step slider, FAVOUR 100 % (step −10) …
 * NEUTRAL (0) … PREVENT 100 % (+10). The step ↔ wire mapping is a non-linear
 * lookup table per field family, derived from the full-year response-curve
 * study of 2026-08 (fw/doc/15min_optimization_history.txt, "UI slider LUT
 * proposal v2"; wire value = old base units / 8):
 *
 *   - PB & SELL: prevention is a long, smooth axis (each doubling of the
 *     penalty removes ~35 % of the remaining SELL quarters; +9 = 1008 base
 *     units still plans ~20 % of them, +10 = sentinel). Below the default the
 *     effect is nil until the penalty reaches zero (step −5) and pure churn
 *     beyond it — there is no "more eager" arbitrage past step −5.
 *   - PBS: penalising it only makes the planner express self-use as PS + PB
 *     instead (saturates by ~+112 base units, ≤ ~30 EUR/yr); favouring it is the
 *     expensive direction (kills grid charging), −10 = "self-use only".
 *   - BUY: the value of grid charging is back-loaded — +9 (1008) leaves only
 *     the really cheap nights (~10 % of quarters) but still 2/3 of the value,
 *     the sentinel then costs the rest. Favouring it is the steepest knob of
 *     all: −1 already adds ~60 % more grid-charge quarters, −9 keeps the
 *     battery charging ~70 % of the year, −10 = "keep full from grid".
 *
 *   The first ±2 steps are meant to be visibly-but-cheaply different from
 *   NEUTRAL on every knob (except FAVOUR on PB/SELL, see above).
 *
 * Keep these as explicit tables — they are meant to be tweaked by hand.
 */

/** Wire unit of the v2 power limits (`max_charge_power` / `max_discharge_power`). */
export const POWER_UNIT_W = 16

/** Largest power limit the 16-bit v2 field can carry, in watts. */
export const MAX_POWER_W = 65535 * POWER_UNIT_W

/** Round a watt value down to the wire unit so that UI → wire → UI does not creep. */
export function floorToPowerUnit(watts: number): number {
  if (!Number.isFinite(watts) || watts <= 0) return 0
  return Math.min(Math.floor(watts / POWER_UNIT_W) * POWER_UNIT_W, MAX_POWER_W)
}

export type PenaltyField = 'penalty_pbs' | 'penalty_pb' | 'penalty_buy' | 'penalty_sell'

export type PenaltyFamily = 'PBS' | 'BUY' | 'PB_SELL'

export type PenaltyBand = 'green' | 'yellow' | 'red'

export const PENALTY_STEP_MIN = -10
export const PENALTY_STEP_MAX = 10
export const PENALTY_PREVENT_SENTINEL = 127

/**
 * Step → wire value, indexed by `step - PENALTY_STEP_MIN` (21 entries, strictly
 * increasing). In firmware base units (×8): PB_SELL −1024/−128/−88/−64/−48/−40/
 * −32/−24/−16/−8/0/+24/+64/+96/+144/+208/+304/+456/+680/+1008/prevent;
 * PBS ±8/16/24/32/40/56/80/144/304, then −1024 / prevent;
 * BUY −1024/−128/−88/−64/−48/−40/−32/−24/−16/−8/0/+16/+32/+56/+96/+160/+256/
 * +400/+640/+1008/prevent.
 */
const PENALTY_LUT: Record<PenaltyFamily, number[]> = {
  //          -100  -90  -80  -70  -60  -50  -40  -30  -20  -10  0   10  20  30  40  50  60  70  80   90  100
  PB_SELL: [-128, -16, -11, -8, -6, -5, -4, -3, -2, -1, 0, 3, 8, 12, 18, 26, 38, 57, 85, 126, 127],
  PBS: [-128, -38, -18, -10, -7, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 7, 10, 18, 38, 127],
  BUY: [-128, -16, -11, -8, -6, -5, -4, -3, -2, -1, 0, 2, 4, 7, 12, 20, 32, 50, 80, 126, 127],
}

export interface PenaltyFieldSpec {
  field: PenaltyField
  family: PenaltyFamily
  /** i18n key of the slider title. */
  title: string
  /** i18n key of the explanatory text under the slider. */
  description: string
  /** PREVENT side never turns red — people can have valid reasons to prevent selling. */
  preventStaysYellow: boolean
}

/** The four sliders, in display (= wire struct) order. */
export const PENALTY_FIELDS: PenaltyFieldSpec[] = [
  {
    field: 'penalty_pbs',
    family: 'PBS',
    title: 'Self-use mode',
    description: '"Self-use" mode. In this mode the battery is discharged for own consumption and surplus solar production is charged into the battery. If you want the battery to act more like a pure solar store, move towards FAVOUR. If you want cheaply charged energy to be held in the battery longer without being used, move the setting slightly towards PREVENT.',
    preventStaysYellow: false,
  },
  {
    field: 'penalty_pb',
    family: 'PB_SELL',
    title: 'Sell production to grid',
    description: '"Sell production to grid" mode. In this mode solar production is not charged into the battery, because the battery is expected to become full and the solar energy is worth more at that moment. If you do not want this to happen so readily, you can move the setting towards PREVENT.',
    preventStaysYellow: true,
  },
  {
    field: 'penalty_buy',
    family: 'BUY',
    title: 'Charge from grid',
    description: '"Buy" mode. In this mode the battery is charged from the grid (and from solar, if there is surplus production). If you think the algorithm charges the battery too full at night, leaving no room for solar production, move slightly towards PREVENT.',
    preventStaysYellow: false,
  },
  {
    field: 'penalty_sell',
    family: 'PB_SELL',
    title: 'Sell from battery to grid',
    description: '"Sell from battery" mode. In this mode the battery is discharged at a higher power than own consumption would require, and electricity is sold to the grid. If you think the algorithm speculates too much, move the slider towards PREVENT. The algorithm will then choose self-use more readily.',
    preventStaysYellow: true,
  },
]

export function penaltySpec(field: PenaltyField): PenaltyFieldSpec {
  const spec = PENALTY_FIELDS.find(s => s.field === field)
  if (!spec) throw new Error(`Unknown penalty field ${field}.`)
  return spec
}

function clampStep(step: number): number {
  return Math.max(PENALTY_STEP_MIN, Math.min(PENALTY_STEP_MAX, Math.round(step)))
}

/** Slider step (−10 … 10) → int8 wire value. */
export function stepToPenalty(field: PenaltyField, step: number): number {
  return PENALTY_LUT[penaltySpec(field).family][clampStep(step) - PENALTY_STEP_MIN]
}

/**
 * int8 wire value → slider step (−10 … 10). Exact for values produced by
 * `stepToPenalty()`; anything else (e.g. set by hand) snaps to the nearest
 * step, so saving would re-encode it to that step's value.
 */
export function penaltyToStep(field: PenaltyField, penalty: number): number {
  const lut = PENALTY_LUT[penaltySpec(field).family]
  const value = Number.isFinite(penalty) ? penalty : 0
  let best = 0
  for (let i = 1; i < lut.length; i++) {
    if (Math.abs(lut[i] - value) < Math.abs(lut[best] - value)) best = i
  }
  return best + PENALTY_STEP_MIN
}

/**
 * Colour band of a slider position: green within ±2 steps, yellow up to ±5,
 * red beyond — except that the PREVENT side of PB/SELL stays yellow all the way.
 */
export function penaltyBand(field: PenaltyField, step: number): PenaltyBand {
  const s = clampStep(step)
  if (Math.abs(s) <= 2) return 'green'
  if (Math.abs(s) <= 5) return 'yellow'
  if (s > 0 && penaltySpec(field).preventStaysYellow) return 'yellow'
  return 'red'
}

/** The worst band among the given slider positions (for the warning box). */
export function worstPenaltyBand(steps: Partial<Record<PenaltyField, number>>): PenaltyBand {
  const order: PenaltyBand[] = ['green', 'yellow', 'red']
  let worst: PenaltyBand = 'green'
  for (const spec of PENALTY_FIELDS) {
    const band = penaltyBand(spec.field, steps[spec.field] ?? 0)
    if (order.indexOf(band) > order.indexOf(worst)) worst = band
  }
  return worst
}

/** "FAVOUR 30%" / "NEUTRAL" / "PREVENT 100%" — `t` translates the three words. */
export function penaltyStepLabel(step: number, t: (key: string) => string): string {
  const s = clampStep(step)
  if (s === 0) return t('NEUTRAL')
  return `${t(s < 0 ? 'FAVOUR' : 'PREVENT')} ${Math.abs(s) * 10}%`
}
